/* -----------------------------------
Copyright: Logical Developments 2025.
Project:   ConNote Portal
Filename:  FreightCards.js
Author:    Dean B. Leggo, John D. Kohl
Version:   0.18
Description:
All functionality to handle and validate customer input of a number of freight details

History:
0.18  27-11-25 JRB   (ld0013647) Removed validation for minimum cubic volume.
0.17  02-05-25 JDK   Added support for PPI freight items.
0.16  04-12-24 JRB   Added handling for dimensions defined by Omnis. Applied to max total fields and cubic volume calculation.
0.15  29-08-24 JRB   Set id to '0' and make sure we have a lineItem before assigning to it.
0.14  27-08-24 JDK   Added reducer action "updateIDs" to return the consignment item IDs to the freight list.
0.13  12-07-23 JRB   added this._warn and handling to prevent going over a max number of lines defined in config
0.12  03-03-23 DBL   Changed the dimension labels to say dimension per item
0.11  23-01-23 JDK   Added field "unitWeight" to allow "weight" to refer to total weight for an item. This allows seamless interaction between desktop and portal.
0.10  18-01-23 JDK   Prevent floating point errors in summary display.
0.09  06-01-23 JRB   Set totals to true if we delete a freight item so we can update to correct totals
0.08  22-11-22 DBL   For select use '' and not -1 as the browser counts that as a selected value
0.07  01-11-22 JRB   (ld0011671) Fixed issue where blank option was not correctly set initially.
0.06  31-10-22 JRB   (ld0011671) Selecting type now also sets the forklift property.
0.05  04-07-22 DBL   Moved number validation to utils.
0.04  28-06-22 JRB   Added a check for numbers ending with . as safari triggers number fields on .
0.03  23-06-22 JRB   Added id to constructor
0.02  15-06-22 JRB   Added dgReducer to loadStore in cardReducer
0.01  11-04-22 DBL   Added support to clear the freight type
0.00	22-03-22 DBL   Created.
----------------------------------- */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMinus, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import CreatableSelect from 'react-select/creatable';
import { freightCardValidation } from '../Configuration/Config';
import * as DG from './DGCards';
import { checkNumber } from './utils';
import useRest from '../Session/useRest';
import { useFullTrailer } from './FullTrailerContext';
import "./FreightCards.css";

import Bugsnag from '@bugsnag/js'
import { getStore } from '../Session/Storage';

class cardInit extends DG.cardInit {
  constructor(type) {
    super(); // don't add a properity that starts with dg.
    this.key = { value: '', error: null };
    this.quantity = { value: '', error: null };
    this.type = { value: type ?? '', error: null };
    this.description = { value: '', error: null };
    this.weight = { value: '', error: null };
    this.unitWeight = { value: '', error: null };
    this.length = { value: '', error: null };
    this.width = { value: '', error: null };
    this.height = { value: '', error: null };
    this.id = { value: '0', error: null }; // Default to 0 so we can correctly handle new items
    this.ppi = { value: '', error: null }; // LD0013215 - PPI item ID
    this.ref = { value: '', error: null }; // LD0013215 - Line item reference #
    this.error = null;
  }
}

class Rate {
  constructor(rate) {
    this.depotFrom = rate?.depotFrom;
    this.depotTo = rate?.depotTo;
    this.default = rate?.default;
    this.freight = rate?.freight;
    this.pickupAllowed = rate?.pickupAllowed;
    this.deliveryAllowed = rate?.deliveryAllowed;
    this.ppiOnly = rate?.ppiOnly;
    this.fullTrailer = rate?.fullTrailer;
  }
}

class init extends DG.init {
  constructor() {
    super();
    let card = new cardInit();
    this.value = [card];
    // this._pkForklift = {value: false, error: null}; // client side only
    // this._dlForklift = {value: false, error: null}; // client side only
    this._totalItems = { value: NaN, error: null }; // client side only
    this._totalWeight = { value: NaN, error: null }; // client side only
    this._totalCubicVolume = { value: NaN, error: null }; // client side only
    this.error = null;
    this._warn = null;
    this._rate = null; // Stores the currently selected freight rate to validate against options changes
    this._fullTrailer = null; // Stores Full Trailer toggle state
    this._restoreState = null;
  }
}


/*
 * The first reducer for Freight Cards
 * The store contains the array of freight cards
 * Action's page - the index to update
 */
function reducer(store, action) {
  const validation = action.validation;
  const ppiList = action.ppiList;
  const maxItems = freightCardValidation.maxItems;
  const options = getStore('options'); // Options contains our unit transformation.

  // console.log(validation);
  // console.log('FreightCards Reducer: store, action', store, action);

  let totals = false;
  let list = store.value.map(card => ({ ...card })); // clone the entire contents of the array and
  // not just the reference to each object
  let error = []; // not using a string because we want line breaks

  switch (action.name) {
    case 'key':
      return { store }; // do not permit changing key values (sequence numbers)

    case 'add':
      if (store._fullTrailer) {
        return store; // Prevent adding items in Full Trailer mode
      }
      if (list.length < maxItems || list.length === 0) {
        list.push(new cardInit(store._rate?.freight));
        if (list.length === maxItems) // If adding this line causes us to have 10 items. Warn the user
          store._warn = `The maximum ${maxItems} line items has been reached.`
        else // Should not be triggered but if we somehow add and are below the number of items then clear the warning.
          store._warn = null
      }
      else {
        store.error = `There are more than ${maxItems} line items`
      }
      break;

    case 'delete':
      if (store._fullTrailer && list.length === 1) {
        return store; // Prevent deleting the last item in Full Trailer mode
      }
      list.splice(action.page, 1);
      totals = true;
      if (list.length === 0)
        list.push(new cardInit());
      if (list.length < maxItems)
        store._warn = null
      break;

    case 'clearFreight':
      list.forEach((card, index, array) =>
        array[index] = cardReducer(card, { page: index, name: 'type', value: '', validation })
      );
      break;

    case 'reset':
      // console.log('reset was called');
      return new init();

    case '_rate':
      // Store the selected rate object for validation when options change
      return { ...store, _rate: action.value }

    case '_fullTrailer':
      return { ...store, _fullTrailer: action.value }

    case '_restoreState':
      return { ...store, _restoreState: action.value }

    case 'id':
      break;

    case 'updateIDs': // LD0012951
      try {
        let freightItemIds = action.value; // Array of objects: { oldID, newID }
        freightItemIds && freightItemIds.forEach((idSet => {
          let lineItem = list.find((lineItem) => lineItem.id.value === idSet.oldID);
          if (lineItem && lineItem.id) lineItem.id.value = idSet.newID;
        }));
      } catch (error) {
        Bugsnag.notify(error.message);
      }
      break;

    case 'loadStore':
      // console.log('loadStore called with action:', action)
      list = action.value.map((card, index) =>
        cardReducer(new cardInit(), { page: index, name: 'loadStore', value: card, validation })
      );
      store._restoreState = true;
      store._rate = action.rate;
      store._fullTrailer = action.rate?.fullTrailer;
      totals = true;
      break;

    case 'ppiError':
      error.push(action.value);
      break;

    case 'dgPackCount': case 'dgPackType': case 'dgTechName': case 'dgClass': case 'dgUNCode':
    case 'dgQty': case 'dgPackGroup': case 'dgSubRisk': case 'dgError': case 'dgDesc': case 'dgClear':
      // dgReducer will only return the dg items
      list[action.page] = { ...list[action.page], ...DG.reducer(list[action.page], action) };
      break;

    // case 'pkForklift': 
    //   store._pkForklift.value = action.value;
    //   break;

    // case 'dlForklift':
    //   store._dlForklift.value = action.value;
    //   break;

    case 'type':
    // fall through

    case 'quantity': case 'weight': case 'length': case 'width': case 'height':
      totals = true;
    // fall through

    default: // replace the row with the new actioned object
      list[action.page] = cardReducer(list[action.page], action);
  }

  // If any of the totals have changed, update all
  if (totals) {
    store._totalItems.value = list.reduce((total, row) =>
      row.quantity.value === ''
        ? total
        : total + Number(row.quantity.value), 0);

    store._totalWeight.value = list.reduce((total, row) =>
      row.weight.value === '' || row.quantity.value === ''
        ? total
        : total + Number(row.weight.value), 0);

    store._totalCubicVolume.value = list.reduce((total, row) =>
      row.height.value === '' || row.length.value === '' || row.width.value === '' || row.quantity.value === ''
        ? NaN
        : total + (Number(row.height.value) / options.dimensions * Number(row.length.value) / options.dimensions *
          Number(row.width.value) / options.dimensions * Number(row.quantity.value)), 0);

    store._totalWeight.value = parseFloat(store._totalWeight.value.toFixed(2));
    store._totalCubicVolume.value = parseFloat(store._totalCubicVolume.value.toFixed(2));

    store._totalWeight.error = store._totalWeight.value > validation.maxTotalWeight
      ? `The total weight exceeds ${validation.maxTotalWeight.toLocaleString()} kg` : null;
    store._totalCubicVolume.error = store._totalCubicVolume.value > validation.maxTotalCubicVolume
      ? `The total cubic volume exceeds ${(validation.maxTotalCubicVolume).toLocaleString()} m³`
      : null
    // (store._totalCubicVolume.value < 0.01 ? `The total cubic volume must be greater than 0.01 m³` : null);
  }

  // Check if all the freight types match
  if (list.some((row, _, array) =>
    row.type.value !== '' && array[0].type.value !== '' && row.type.value !== array[0].type.value
  )) {
    error.push('All freight types must match');
    error.push(<br />);
  }

  // // Validate PPI selections - this doesn't work well when reloading state...
  // if (list.some((item) => {
  //   return item.ppi.value && !ppiList?.some((ppiEntry) => Number(item.ppi.value) === ppiEntry.id) // true if any item has a PPI value, but no entry in the ppiList matches that value
  // })) {
  //   error.push("Invalid freight selection.");
  // }

  // Validate PPI limits (if there is a PPI List)
  ppiList?.forEach((ppiEntry) => {
    let matches = list.filter((item) => Number(item.ppi.value) === ppiEntry.id);
    if (matches.length) {
      let quantity = matches.reduce((previousValue, currentEntry) => previousValue + Number(currentEntry.quantity.value), 0);
      if (ppiEntry.minimum && quantity < ppiEntry.minimum) {
        error.push(`Quantity for ${ppiEntry.name} must be greater than ${ppiEntry.minimum}.`);
        error.push(<br/>);
      }
      if (ppiEntry.maximum && quantity > ppiEntry.maximum) {
        error.push(`Quantity for ${ppiEntry.name} must be less than ${ppiEntry.maximum}.`);
        error.push(<br/>);
      }
    }
  })

  // Check if any items are exclusive PPI, and ensure that freight is not mixed if so.
  let exclusivePPI = ppiList?.find((ppiEntry) => ppiEntry.exclusive && list.some((item) => Number(item.ppi.value) === ppiEntry.id)); // exclusive PPI = first ppiEntry which is exclusive and there is a freight item of it.
  // let exclusivePPI = list.find((item) => ppiList?.some((entry) => entry.id === item.ppi));
  if (exclusivePPI && list.some((item) => Number(item.ppi.value) !== exclusivePPI.id)) { // An exclusive PPI was found and there are items which are not instances of it.
    error.push(`${exclusivePPI.name} cannot be booked with other freight.`);
  }

  // Bubble up any errors in the totals
  ['_totalItems', '_totalWeight', '_totalCubicVolume'].forEach(name => {
    if (store[name].error) {
      error.push(store[name].error);
      error.push(<br />);
    }
  });

  // Bubble up any errors from the freight cards into the parents error
  list.forEach((key, index) => {
    if (key.error !== null) {
      error.push(`Item ${index + 1}: ${key.error}`);
      error.push(<br />);
    }
  });

  if (error.length === 0) error = null; // clear string if no errors were found
  //else error.push(`Fix the issue or ${callError}`); The parent element should determine this

  // Do any super init processing
  const supers = DG.initProcess(list);

  return { ...store, ...supers, value: list, error: error };
}


/*
 * A Single reducer for each card
 * Receive the array (store property) from the reducer above and the action
 */
function cardReducer(store, action) {
  const validation = action.validation;
  const options = getStore('options');
  const lengthUnit = (options.dimensions === 1 ? 'm' : 'cm')
  // console.log('FreightCards card Reducer: store, action', store, action);
  // console.log(options);

  let error = null;
  let freightError = '';
  let newValue = action.value;

  switch (action.name) {
    case 'quantity':
      [newValue, error] = checkNumber(newValue, 'Quantity', { lower: 1 })
      break;

    case 'type':
      // no validation done at this level
      break;

    case 'id':
      break;

    case 'description':
      if (newValue.length > 200)
        error = `Description can not be greater than 200 characters`;
      break;

    case 'unitWeight':
      if (newValue) [newValue, error] = checkNumber(newValue, 'Unit weight', { lower: 0.01, upper: validation.maxTotalWeight * options.dimensions, digits: 2 }, 'kg');
      break;

    case 'weight':
      if (newValue) [newValue, error] = checkNumber(newValue, 'Weight', { lower: 0.01, upper: validation.maxTotalWeight * options.dimensions, digits: 2 }, 'kg');
      break;

    case 'length':
      if (newValue) [newValue, error] = checkNumber(newValue, 'Length', { lower: 0.01, upper: validation.maxLength * options.dimensions, digits: 2 }, lengthUnit);
      break;

    case 'width':
      if (newValue) [newValue, error] = checkNumber(newValue, 'Width', { lower: 0.01, upper: validation.maxWidth * options.dimensions, digits: 2 }, lengthUnit);
      break;

    case 'height':
      if (newValue) [newValue, error] = checkNumber(newValue, 'Height', { lower: 0.01, upper: validation.maxHeight * options.dimensions, digits: 2 }, lengthUnit);
      break;

    case 'ppi': case 'ref':
      break;

    case 'loadStore':
      let unitWeight = newValue.unitWeight
        ? newValue.unitWeight
        : ((newValue.weight && newValue.quantity)
          ? newValue.weight / newValue.quantity
          : ''); // calculate the unit weight - weight per item - as this is no longer the value sent to omnis.
      store = {
        ...cardReducer({}, { page: action.page, name: 'quantity', value: newValue.quantity, validation }),
        ...cardReducer({}, { page: action.page, name: 'type', value: newValue.type, validation }),
        ...cardReducer({}, { page: action.page, name: 'description', value: newValue.description, validation }),
        ...cardReducer({}, { page: action.page, name: 'weight', value: newValue.weight, validation }),
        ...cardReducer({}, { page: action.page, name: 'unitWeight', value: unitWeight, validation }),
        ...cardReducer({}, { page: action.page, name: 'length', value: newValue.length, validation }),
        ...cardReducer({}, { page: action.page, name: 'width', value: newValue.width, validation }),
        ...cardReducer({}, { page: action.page, name: 'height', value: newValue.height, validation }),
        ...cardReducer({}, { page: action.page, name: 'id', value: newValue.id, validation }),
        ...cardReducer({}, { page: action.page, name: 'ppi', value: newValue.ppi, validation }),
        ...cardReducer({}, { page: action.page, name: 'ref', value: newValue.ref, validation }),
        ...DG.reducer({}, { name: 'loadStore', value: newValue })
      }
      break; // allow the errors to bubble up

    default:
      throw new Error(`Freight Card (${action.name}) is not supported`);
  }

  // Save all values as strings to prevent ugly number entry
  newValue = newValue ? '' + newValue : '';

  // Bubble up any errors from the store into the freightCard, including the actioned error
  Object.keys(store).forEach(key => {
    if (key === action.name) {
      if (error) freightError += error + ', ';
    }
    else if (key !== 'error' && key !== '_dgError' && store[key].error !== null && !key.match(/^dg/))
      freightError += store[key].error + ', ';
  });
  if (freightError === '')
    freightError = null; // clear string if no errors were bubbled
  else
    freightError = freightError.slice(0, -2); // remove the last ', '


  // return the entire row with the updated action
  if (action.name === 'loadStore')
    return { ...store, error: freightError }
  else
    return { ...store, [action.name]: { value: newValue, error: error }, error: freightError }
}

/*
 * Return true if the stored rate selection is available in the current options object
 * This prevents stale rate selections when sender/receiver details change and
 * new freight options become available
 */
function isStoredRateValid(storedRate, options) {
  let exists = true;
  if (storedRate && options) {
    exists = options?.type.some(rate => Object.keys(rate).every((key) => storedRate[key] === rate[key]));
  }

  return exists;
}


/*
 * A single Freight Card
 * Added setIsPPI to allow FreightCard to trigger PPI mode when ppiOnly rates are selected
 */
function FreightCard({
  freightItem,
  index,
  dispatcher,
  dispatcherAction,
  options,
  validation,
  validRate,
  store,
  ppiRequest,
  ppiLoading,
  ppiList,
  isFullTrailer,
  fullTrailerRates
}) {
  // const valid = validation ? validation : freightCardValidation;
  const sessionOptions = getStore('options');
  const lengthUnit = (sessionOptions.dimensions === 1 ? 'm' : 'cm')

  // Determine field locking for Full Trailer mode
  const quantityLocked = isFullTrailer;
  const descriptionLocked = isFullTrailer;
  const typeLocked = isFullTrailer && fullTrailerRates?.length === 1;

  /**
   * Format PPI options for react-select Creatable
   */
  const formatPPIOptions = useCallback(() => {
    if (!ppiList) return [];

    return ppiList.map(item => ({
      value: String(item.id),
      label: item.name,
      ppiData: item // Store full PPI object for exclusive check
    }));
  }, [ppiList]);

  /**
   * Get currently selected option for react-select
   */
  const getSelectedOption = useCallback(() => {
    const description = freightItem.description?.value;
    const ppiValue = freightItem.ppi?.value;

    if (!description) return null;

    // If ppi is set, find matching PPI option
    if (ppiValue && ppiValue !== '') {
      const ppiItem = ppiList?.find(item => String(item.id) === ppiValue);
      if (ppiItem) {
        return {
          value: String(ppiItem.id),
          label: ppiItem.name,
          ppiData: ppiItem
        };
      }
    }

    // If description exists but no PPI match, it's custom text
    // Return as free-form value (not in options list)
    if (description && (!ppiValue || ppiValue === '')) {
      return {
        value: '',
        label: description,
        __isNew__: true // Mark as custom input for react-select
      };
    }

    return null;
  }, [freightItem.description?.value, freightItem.ppi?.value, ppiList]);

  /**
   * Handle selection change (PPI option selected from dropdown)
   */
  const handleDescriptionChange = useCallback((selectedOption) => {
    if (!selectedOption) {
      // Cleared selection
      dispatcher({ ...dispatcherAction, value: { ppiList, page: index, name: 'ppi', value: '', validation } });
      dispatcher({ ...dispatcherAction, value: { ppiList, page: index, name: 'description', value: '', validation } });
      return;
    }

    // User selected a PPI option from dropdown
    // const ppiItem = selectedOption.ppiData;

    dispatcher({ ...dispatcherAction, value: { ppiList, page: index, name: 'ppi', value: selectedOption.value, validation } });
    dispatcher({ ...dispatcherAction, value: { ppiList, page: index, name: 'description', value: selectedOption.label, validation } });

    // Removed - we can use validation to tell the user there's an error which provides better UX than their freight just disappearing.
    // // Check for exclusive PPI
    // if (ppiItem?.exclusive && store.value.length > 1) {
    //   // console.log('exclusive ppi check calling reset');
    //   dispatcher({ ...dispatcherAction, value: { ppiList, page: 0, name: 'reset' } });
    // }
  }, [dispatcher, dispatcherAction, ppiList, index, validation]);
  // }, [dispatcher, dispatcherAction, ppiList, index, validation, store.value.length]);

  /**
   * Handle creation of new custom option (user typed custom text)
   */
  const handleCreateCustomOption = useCallback((inputValue) => {
    const customDesc = inputValue.trim();

    if (!customDesc || isFullTrailer || store._rate?.ppiOnly) {
      return; // Blocked by isValidNewOption, but double-check
    }

    // Set custom description directly (no PPI id, not added to dropdown)
    dispatcher({ ...dispatcherAction, value: { ppiList, page: index, name: 'ppi', value: '', validation } });
    dispatcher({ ...dispatcherAction, value: { ppiList, page: index, name: 'description', value: customDesc, validation } });
  }, [dispatcher, dispatcherAction, ppiList, index, validation, isFullTrailer, store._rate?.ppiOnly]);

  useEffect(() => {
    // Load PPI list when freight type exists but list is not loaded yet
    // Allow PPI request in BOTH standard and Full Trailer modes
    if (!ppiList && freightItem?.type?.value) {
      // console.log('getting ppi list', ppiList);
      let rate = options?.type.find(rate => rate.freight === freightItem.type.value);
      if (!ppiLoading && rate) ppiRequest({ rate: rate });
    }
  }, [freightItem?.type?.value, options?.type, ppiList, ppiRequest, ppiLoading])

  useEffect(() => {
    if (freightItem.quantity && freightItem.unitWeight) dispatcher({
      ...dispatcherAction, value: {
        ppiList, page: index, name: 'weight', value: (freightItem.quantity.value * freightItem.unitWeight.value), validation
      }
    });
  }, [freightItem.quantity, freightItem.unitWeight]) // eslint-disable-line

  const handleChange = useCallback((e) => {
    dispatcher({ ...dispatcherAction, value: { ppiList, page: index, name: e.target.name, value: e.target.value, validation } });
  }, [dispatcher, dispatcherAction, ppiList, index, validation]);

  const handleInput = (inputName, classes) => {
    return ({
      id: index + inputName,
      name: inputName,
      value: freightItem[inputName].value,
      className: freightItem[inputName].error ? classes + ' error-outline' : classes,
      onChange: handleChange
    });
  };

  const handleSelect = useCallback((inputName, classes) => {
    // console.log('rate select handler');
    if (options) {
      let idx = options.type.findIndex(t => t.freight === freightItem[inputName].value); // Use the index to work out which value to display.
      if (idx === -1) idx = '';
      return ({
        id: index + inputName,
        name: inputName,
        value: idx,
        className: freightItem[inputName].error ? classes + ' error-outline' : classes,
        onChange: e => {
          // Clear current PPI selections
          dispatcher({ ...dispatcherAction, value: { ppiList, page: index, name: 'ppi', value: '', validation } });
          dispatcher({ ...dispatcherAction, value: { ppiList, page: index, name: 'description', value: '', validation } });

          // Set freight type
          dispatcher({ ...dispatcherAction, value: { ppiList, page: index, name: e.target.name, value: options.type[e.target.value].freight, validation } });

          // Request PPI list for selected freight type (both standard and Full Trailer modes)
          if (options?.type[e.target.value]) {
            ppiRequest({ rate: options.type[e.target.value] });
          }

          // Store the selected rate for validation when options change
          if (validRate && options?.type[e.target.value]) {
            dispatcher({
              ...dispatcherAction, value: {
                ppiList,
                page: 0,
                name: '_rate',
                value: options.type[e.target.value],
              }
            });
          }

          // Check if this PPI selection is exclusive and needs to clear other freight
          // Note: This check will happen after PPI is selected, not here
        }
      });
    }
  }, [validRate, freightItem, index, dispatcher, dispatcherAction, ppiList, options, validation, ppiRequest])

  const handleDelete = () => dispatcher({ ...dispatcherAction, value: { ppiList, page: index, name: 'delete', value: null, validation } });

  return (
    <div id={`freightCard${index}`} className='freightCard stack'>
      {/* <h3>Line Item {index + 1}</h3> */}
      <div className="cluster">
        <h3>Line Item {index + 1}</h3>
      </div>
      <div className="cluster cluster-no-wrap">
        <div className="stack">
          <button
            type='button'
            title={`Delete freight item ${index + 1}`}
            className="btn btn__delete"
            onClick={handleDelete}
          >
            <FontAwesomeIcon icon={faMinus} />
          </button>
          <div className="error-msg" style={{ visibility: freightItem.error ? '' : 'hidden' }}>
            <FontAwesomeIcon icon={faTriangleExclamation} size="2x" />
          </div>
        </div>
        <div className="grid four-col flex-grow">
          <div className="stack">
            <label htmlFor={index + 'quantity'}><abbr title='Quantity'>No.</abbr> of Items</label>
            <input type='number' step='1' required
              min={1}
              max={validation.maxItems ? validation.maxItems : undefined}
              {...handleInput('quantity')}
              disabled={quantityLocked}
              readOnly={quantityLocked}
            />
          </div>
          <div className="stack">
            <label htmlFor={index + 'type'}>Freight Type</label>
            <select id='type' required {...handleSelect('type')} disabled={typeLocked}>
              <option hidden disabled value=''></option>
              {options && options !== false && options.type.map((item, i) =>
                (<option value={i} key={`R${index}-${i}`}>{item.freight}</option>))
              }
            </select>
          </div>
          <div className="stack" style={{ gridColumn: "span 2" }}>
            <label htmlFor={index + "description"}>Description</label>
            {/* Use CreatableSelect for PPI options + custom descriptions */}
            <CreatableSelect
              inputId={index + "description"}
              classNamePrefix="ReactSelect"
              placeholder={(() => { // self-invoking function. Neater than nested ternaries.
                if (!store._rate) {
                  return "Please select a Freight Type"
                } else if (ppiLoading) {
                  return "Loading..."
                } else if (store._rate?.ppiOnly) {
                  return "Select an option"
                } else {
                  return "Select or type description"
                }
              })()}
              // placeholder={ppiLoading ? "Loading..." : (store._rate?.ppiOnly ? "Select an option" : "Select or type description") }
              isDisabled={descriptionLocked || ppiLoading || !store._rate}
              isLoading={ppiLoading}
              isClearable={false}
              isSearchable={true}
              options={formatPPIOptions()}
              value={getSelectedOption()}
              onChange={handleDescriptionChange}
              onCreateOption={handleCreateCustomOption}
              formatCreateLabel={(inputValue) => `Use: "${inputValue}"`}
              isValidNewOption={(inputValue) => {
                // Only allow custom options in standard mode (not Full Trailer)
                if (isFullTrailer) return false;
                // Don't allow if ppiOnly rate
                if (store._rate?.ppiOnly) return false;
                // Require non-empty trimmed input
                return inputValue && inputValue.trim().length > 0;
              }}
              noOptionsMessage={() =>
                ppiLoading ? "Loading..." :
                  (isFullTrailer || store._rate?.ppiOnly) ? "No options available" :
                    "Type to add custom description"
              }
              styles={{
                control: (base) => ({
                  ...base,
                  borderColor: freightItem.description?.error ? 'var(--clr-error, red)' : base.borderColor,
                }),
              }}
              required={true}
            />
          </div>
          <div className="stack">
            <label htmlFor={index + 'unitWeight'}>Weight/item (kg)</label>
            <input type='number' step='0.01' required min={0.01} max={validation.maxTotalWeight}
              {...handleInput('unitWeight')}
            />
          </div>
          <div className="stack">
            <label htmlFor={index + 'length'}>Length/item ({lengthUnit})</label>
            <input type='number' step='0.01' required min={0.01} max={validation.maxLength * sessionOptions.dimensions}
              {...handleInput('length')}
            />
          </div>
          <div className="stack">
            <label htmlFor={index + 'width'}>Width/item ({lengthUnit})</label>
            <input type='number' step='0.01' required min={0.01} max={validation.maxWidth * sessionOptions.dimensions}
              {...handleInput('width')}
            />
          </div>
          <div className="stack">
            <label htmlFor={index + 'height'}>Height/item ({lengthUnit})</label>
            <input type='number' step='0.01' required min={0.01} max={validation.maxHeight * sessionOptions.dimensions}
              {...handleInput('height')}
            />
          </div>
          <div className="stack" style={{ gridColumn: "span 4" }}>
            <label htmlFor={index + 'ref'}>Item Reference</label>
            <input type='text' maxLength={100}
              {...handleInput('ref')}
            />
          </div>
        </div>
      </div>
    </div>
  );
}


/*
 * The Freight Details section containg the list of freight cards
 * options - is an object with the parameters:
 * type - an array of strings that are the available freight types  
 */
function FreightCards({ store, dispatcher, dispatcherAction, title, options, validation }) {
  const [ppiRequest, ppiResponse, , ppiLoading] = useRest('POST', 'options/ppi');
  const [ppiList, setPPIList] = useState(null);
  const [validRate, setValidRate] = useState(true);
  const [restoreState, setRestoreState] = useState(null);
  const [restoreProgress, setRestoreProgress] = useState(0);

  // Full Trailer context (hook always called, returns no-ops when disabled)
  const { isEnabled, isFullTrailer, fullTrailerRates, isAvailable, setFullTrailer } = useFullTrailer();

  // useEffect(() => {
  //   console.log(store);
  // }, [store])
  
  // Compute effective options based on Full Trailer mode
  const effectiveOptions = useMemo(() => {
    // console.log(options, fullTrailerRates);
    if (isFullTrailer && fullTrailerRates.length > 0) {
      // Convert fullTrailerRates to match expected options.type structure
      return { type: fullTrailerRates };
    }
    // Filter the standard options, as these include full trailer options to ensure address selections work correctly.
    let standardOptions = options?.type?.filter(option => !option.fullTrailer);
    /**
     * Filter available options, where availability is determined by the following algorithm:
     * - If any rate is for the customer's price list (i.e. not default), a rate is available if it is not default
     * - Otherwise, all rates are available.
     */    
    let availableOptions = (standardOptions?.some(option => !option.default))
      ? standardOptions.filter(option => !option.default)
      : standardOptions;
    return { ...options, type: availableOptions || [] }
  }, [isFullTrailer, fullTrailerRates, options]);

  // const sessionOptions = getStore('options');
  // const lengthUnit = (sessionOptions.dimensions === 1 ? 'm' : 'cm')
  // monitor the returned value from the ppiRequest POST

  useEffect(() => {
    // console.log(store?._rate);
    // Skip validation in Full Trailer mode
    if (isFullTrailer) {
      setValidRate(true);
      return;
    }

    if (!store._restoreState) { // don't try to validate if state is being restored.
      let isValid = isStoredRateValid(store?._rate, options);
      // console.log('determining rate validity:', store?._rate, options, isValid);
      setValidRate(isValid);
    }
  }, [options, store._fullTrailer, store._restoreState, store._rate, dispatcher, dispatcherAction, isFullTrailer, setFullTrailer])

  useEffect(() => {
    // console.log('ppi api use effect')
    // Process PPI API response for BOTH standard and Full Trailer modes

    if (ppiResponse?.error) { // error: dispatch it to the reducer, and set the list to null
      dispatcher({ ...dispatcherAction, value: { ppiList, page: 0, name: 'ppiError', value: ppiResponse.error } });
      setPPIList(null);
    } else if (ppiResponse?.ppi !== undefined) { // Check if ppi property exists (could be [] or [items])
      setPPIList(ppiResponse.ppi); // Set to empty array [] if no options, or array with items
    }
  }, [ppiResponse, dispatcher, dispatcherAction, ppiList]); // dispatcher & dispatcherAction are constant and should not trigger this effect.

  // Request PPI when freight type changes in Full Trailer mode
  useEffect(() => {
    if (!isFullTrailer || !fullTrailerRates.length) return;

    const firstItem = store.value[0];
    const selectedType = firstItem?.type?.value;

    // Only request if ppiList is null (not loaded yet)
    // Empty array [] means "loaded but no options available" - don't request again
    if (selectedType && ppiList === null) {
      // Find rate and request PPI from API
      const rate = fullTrailerRates.find(r => r.freight === selectedType);
      if (rate && !ppiLoading) {
        ppiRequest({ rate });
      }
    }
  }, [isFullTrailer, fullTrailerRates, store.value, ppiList, ppiRequest, ppiLoading]);

  // Auto-select first PPI when list loads in Full Trailer mode
  useEffect(() => {
    if (!isFullTrailer || !ppiList || ppiList.length === 0) return;

    const firstItem = store.value[0];

    // Check if we need to auto-select
    const currentPPI = firstItem?.ppi?.value;
    const ppiExists = currentPPI && ppiList.some(p => String(p.id) === currentPPI);

    if (!ppiExists && firstItem?.type?.value) {
      // Auto-select first PPI
      const firstPPI = ppiList[0];

      dispatcher({
        ...dispatcherAction,
        value: { ppiList, page: 0, name: 'ppi', value: String(firstPPI.id), validation }
      });

      dispatcher({
        ...dispatcherAction,
        value: { ppiList, page: 0, name: 'description', value: firstPPI.name, validation }
      });

      dispatcher({
        ...dispatcherAction,
        value: { ppiList, page: 0, name: 'quantity', value: '1', validation }
      });
    }
  }, [isFullTrailer, ppiList, store.value, dispatcher, dispatcherAction, validation]);

  // Reset PPI state when options change (e.g., when sender/receiver details change)
  // and the previously selected rate is no longer available in the new options
  useEffect(() => {
    // Skip reset in Full Trailer mode (rates are managed separately)
    if (isFullTrailer) return;

    if (!validRate) {
      setPPIList(null);

      // console.log('invalid rate calling reset');
      dispatcher({ ...dispatcherAction, value: { ppiList, page: 0, name: '_rate', value: null } });
      dispatcher({ ...dispatcherAction, value: { ppiList, page: 0, name: 'reset', value: true } });
    }
  }, [validRate, dispatcher, dispatcherAction, ppiList, isFullTrailer])

  // This is ugly as sin. However, due to how we handle freight in general, it is the only method I have found which restores a PPI item.
  useEffect(() => {
    if (store?._restoreState) { // set when the reducer is called with the 'loadStore' action and a rate with fullTrailer: true.
      if (!restoreState) {
        setRestoreState({ rate: store?._rate, items: store?.value }); // preserve values to store in case other effects lose the main values.
        setRestoreProgress(1); // Starts at zero. The full trailer state restoration will begin at step one, after the restoreState has been set.
      }
      if (restoreProgress && store?._rate.fullTrailer) {
        switch (restoreProgress) { // step-wise process. Each step triggers changes in the data, which will re-trigger the effect. Each step is gated to prevent progress until the previous step has actually completed.
          case 1:
            setFullTrailer(true);
            setRestoreProgress(2);
            break;
          case 2:
            // console.log('restore step 2',fullTrailerRates)
            if (fullTrailerRates?.length) { // Rates is an array - it may be empty. Require that there are actually rates there to compare.
              const fullTrailerRate = fullTrailerRates.find(r => r.freight === restoreState.rate.freight);
              if (fullTrailerRate) {
                ppiRequest({ rate: fullTrailerRate });
              }

              setRestoreProgress(3);
            }
            break;
          case 3:
            // console.log(ppiList);
            if (ppiList !== null) {
              const firstItem = restoreState.items[0];
              const ppiEntry = ppiList.find(entry => entry.id = firstItem.ppi.value);
              
              // console.log(ppiList, firstItem, ppiEntry);

              dispatcher({...dispatcherAction,
                value: { ppiList, page: 0, name: 'ppi', value: ppiEntry?.id}
              })
              setRestoreProgress(4);
            }
            break;
          default: // All steps complete, 
            dispatcher({ ...dispatcherAction, value: { ppiList, page: 0, name: '_restoreState', value: false } });
            setRestoreState(0);
        }
      } else if (!store?._fullTrailer && store?._rate) { // not a full trailer booking, but loadData was called. Once the _rate property has been set, flag that state has been restored.
        // console.log('clearing restore state');
        dispatcher({ ...dispatcherAction, value: { ppiList, page: 0, name: '_restoreState', value: false } });
      }
    }
  }, [store?.value, store?._fullTrailer, store?._restoreState, store?._rate, validation, dispatcher, dispatcherAction, ppiList, ppiRequest, setFullTrailer, fullTrailerRates, restoreState, restoreProgress])

  const handleFullTrailer = useCallback(() => {
    const newValue = !isFullTrailer;
    setFullTrailer(newValue);

    // console.log('full trailer toggle calling reset');
    // Reset freight list when toggling
    dispatcher({
      ...dispatcherAction,
      value: { ppiList, page: 0, name: 'reset' }
    });

    dispatcher({
      ...dispatcherAction,
      value: { ppiList, page: 0, name: '_fullTrailer', value: newValue }
    });

    // If toggling ON and rates available
    if (newValue && fullTrailerRates.length > 0) {
      const firstRate = fullTrailerRates[0];

      // Auto-select first freight type
      dispatcher({
        ...dispatcherAction,
        value: {
          ppiList,
          page: 0,
          name: 'type',
          value: firstRate.freight,
          validation
        }
      });

      // Call PPI API with Full Trailer rate
      ppiRequest({ rate: firstRate });

      // Store rate for validation
      dispatcher({
        ...dispatcherAction,
        value: {
          ppiList,
          page: 0,
          name: '_rate',
          value: firstRate
        }
      });

      // Note: PPI auto-selection will happen in separate effect after API responds

    } else if (!newValue) {
      // Toggling OFF - clear PPI list
      setPPIList(null);
    }
  }, [isFullTrailer, fullTrailerRates, setFullTrailer, dispatcher, dispatcherAction, ppiList, validation, ppiRequest]);
  
  /**
   * If the available options indicate that the from & to selections do not support $/kg rates, call the Full Trailer handler.
   * This has the same effect as the user clicking Full Trailer.
   * In this scenario, Full Trailer cannot be unchecked unless the from & to selections change, as the application will re-run this effect.
   */
  useEffect(() => {
    // console.log(isAvailable, isFullTrailer, effectiveOptions, options);
    if (isAvailable & !isFullTrailer && effectiveOptions?.type?.length === 0 && options?.type?.length > 0) {
      // console.log('auto-setting full trailer');
      handleFullTrailer();
    }
  }, [isAvailable, isFullTrailer, effectiveOptions, options, setFullTrailer, handleFullTrailer]);

  const handleAdd = useCallback(() => {
    dispatcher({ ...dispatcherAction, value: { ppiList, page: 0, name: 'add', value: null, validation } })
  }, [dispatcher, dispatcherAction, ppiList, validation]);

  // // Helper to check if any freight item has exclusive PPI selected
  // const hasExclusivePPI = useCallback(() => {
  //   if (!ppiList) return false;
  //   return store.value.some(item => {
  //     if (!item.ppi?.value || item.ppi.value === '') return false; // Skip custom descriptions
  //     const ppiItem = ppiList.find(p => `${p.id}` === item.ppi.value);
  //     return ppiItem?.exclusive || false;
  //   });
  // }, [ppiList, store.value]);

  return (
    <section className="freightDetails accent stack">
      {title}
      {isEnabled && <div className="freight-item-header">
        <label htmlFor="enable_full_trailer" className="toggle-switch">
          <input
            id="enable_full_trailer"
            name="enable_full_trailer"
            type="checkbox"
            checked={isFullTrailer}
            onChange={handleFullTrailer}
            disabled={!isAvailable}
          />
          <span className="toggle-slider round"></span>
          Full Trailer Booking
        </label>
      </div>}
      <div className='stack'>
        <div className="cluster cluster-no-wrap">
          {!isFullTrailer && <button
            type='button'
            id='btnAddFreight'
            title='Add a new freight item'
            className={`btn btn__add margin-block-end${store.value.length >= freightCardValidation.maxItems ? ' disabled' : ''}`}
            onClick={handleAdd}
            disabled={store.value.length >= freightCardValidation.maxItems}
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>}
          <div className="stack flex-grow">
            {store.value.map((card, index) =>
              <FreightCard freightItem={card} index={index} dispatcher={dispatcher} store={store}
                dispatcherAction={dispatcherAction} options={effectiveOptions} key={`FC${index}`} validation={validation}
                ppiRequest={ppiRequest} ppiList={ppiList} ppiLoading={ppiLoading} validRate={validRate}
                isFullTrailer={isFullTrailer} fullTrailerRates={fullTrailerRates}
              />
            )}
          </div>
        </div>
      </div>
      <div className="stack">
        <div className="cluster cluster-end">
          <p className='h3' htmlFor="totalWeight">Total Number of Items:</p>
          <p id="totalWeight">{isNaN(store._totalItems.value) ? '' : store._totalItems.value}</p>
        </div>
        <div className="cluster cluster-outer">
          <div className="error-msg margin-inline-start-auto"
            style={{ visibility: store._totalWeight.error ? '' : 'hidden' }}
          >
            <FontAwesomeIcon icon={faTriangleExclamation} size="2x" />
          </div>
          <div className="cluster cluster-end">
            <p className='h3' htmlFor="totalWeight">Total Weight:</p>
            <p id="totalWeight">{isNaN(store._totalWeight.value) ? '' : `${store._totalWeight.value} kg`}</p>
          </div>
        </div>
        <div className="cluster cluster-outer">
          <div className="error-msg margin-inline-start-auto"
            style={{ visibility: store._totalCubicVolume.error ? '' : 'hidden' }}
          >
            <FontAwesomeIcon icon={faTriangleExclamation} size="2x" />
          </div>
          <div className='cluster cluster-end'>
            <p className='h3' htmlFor="totalCubicVolume">Total Cubic Volume:</p>
            <p id="totalCubicVolume">
              {isNaN(store._totalCubicVolume.value) ? '' : `${store._totalCubicVolume.value} m³`}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}


/*
 * The Freight Summary section containing the list of freight items
 * store - the freight list from FreightCards.js
 * title - optional title to display
 * edit - optional callback function for an edit button 
 */
function FreightSummary({ store, title, edit }) {
  const sessionOptions = getStore('options');
  // const lengthUnit = (sessionOptions.dimensions === 1 ? 'm' : 'cm')

  const cubicSummary = store.value.reduce((total, row) =>
    row.height.value === '' || row.length.value === '' || row.width.value === '' || row.quantity.value === ''
      ? 0
      : total + (Number(row.height.value) / sessionOptions.dimensions * Number(row.length.value) / sessionOptions.dimensions *
        Number(row.width.value) / sessionOptions.dimensions * Number(row.quantity.value)), 0).toFixed(2);



  return (
    <section className='stack accent'>
      <div className='cluster cluster-outer'>
        {title}
        {edit && <button type='button' className='btn__link' onClick={edit}>Edit</button>}
      </div>
      <table>
        <thead>
          <tr>
            <th>QTY</th>
            <th>Freight Type</th>
            <th>Description</th>
            <th>Reference</th>
            <th>Weight</th>
            <th>Length</th>
            <th>Width</th>
            <th>Height</th>
          </tr>
        </thead>
        <tbody>
          {store.value.map((freight, index) =>
            <tr key={`FS${index}`}>
              <td>{freight.quantity.value}</td>
              <td>{freight.type.value}</td>
              <td>{freight.description.value}</td>
              <td>{freight.ref.value}</td>
              <td>{freight.unitWeight.value}</td>
              <td>{freight.length.value}</td>
              <td>{freight.width.value}</td>
              <td>{freight.height.value}</td>
            </tr>
          )}
        </tbody>
      </table>
      <div className='cluster cluster-end'>
        <p>Total <abbr title='number'>No.</abbr> Items:</p>
        <p>{store._totalItems.value.toString()}</p>
      </div>
      <div className='cluster cluster-end'>
        <p>Total Weight:</p>
        <p>{store._totalWeight.value.toString()} kg</p>
      </div>
      <div className='cluster cluster-end'>
        <p>Total Cubic Volume:</p>
        <p>{cubicSummary.toString()} m³</p>
      </div>
    </section>
  );
}

export default FreightCards;
export { init, Rate, reducer, FreightSummary };